import { AudioMessage, AudioMessage_Type } from "../generated/audio_message";
import { TokenType, getDecryptedJwtAndType } from "../utils/tokenEncryptionUtils";
import { createConnectMessage, isServerConnected, createOutgoingAudioMessage } from "./protobuf_utils";
import { AudioMessage_SpeakerInfo, AudioMessage_Command, AudioMessage_Command_Type, AudioMessage_ServerInfo_HealthStatus } from "../generated/audio_message";
import { ListenerRegistry } from "./listenerRegistry"

export type ConnectionStatus =
  | "disconnected"
  | "connecting"
  | "handshaking"
  | "connected"
  | "error"
  | "auto-reconnecting";

export type ConnectionStatusListener = (status: ConnectionStatus) => void;
export type ReconnectCountdownListener = (count: number) => void;
export type SpeakersListener = (speakers: AudioMessage_SpeakerInfo[]) => void;
export type FocussedSpeakerListener = (speaker: AudioMessage_SpeakerInfo | undefined) => void;
export type ListeningStatusListener = (isListening: boolean) => void;
export type HealthStatusListener = (healthStatus: AudioMessage_ServerInfo_HealthStatus) => void;
export type CommandListener = (cmd: AudioMessage_Command) => void;

export class WebSocketConnection {
  public serverUrl: string;
  public joinToken: string | null = null;
  private socket: WebSocket | null = null;
  private status: ConnectionStatus = "disconnected";

  private errorMessage: string | undefined = undefined;
  private isServerListening: boolean = false;
  public speakers: AudioMessage_SpeakerInfo[] = [];
  public focussedSpeaker: AudioMessage_SpeakerInfo | undefined = undefined;

  // Auto-reconnect properties
  private autoReconnectEnabled: boolean = true;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private currentReconnectDelay: number = 1000; // Start with 1 second
  private maxReconnectDelay: number = 8000; // Max 8 seconds
  private reconnectCountdown: number = 0;
  // Connection timeout to prevent hanging in connecting state
  private connectionTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly CONNECTION_TIMEOUT_MS: number = 10000; // 10 seconds

  // Various notifications delivered via callback
  private statusListeners = new ListenerRegistry<ConnectionStatusListener>();
  private reconnectCountdownListeners = new ListenerRegistry<ReconnectCountdownListener>();
  private speakersListeners = new ListenerRegistry<SpeakersListener>();
  private focussedSpeakerListeners = new ListenerRegistry<FocussedSpeakerListener>();
  private listeningStatusListeners = new ListenerRegistry<ListeningStatusListener>();
  private healthStatusListeners = new ListenerRegistry<HealthStatusListener>();
  private commandListeners = new Map<AudioMessage_Command_Type, ListenerRegistry<CommandListener>>();

  // Singleton instance
  private static instance: WebSocketConnection | null = null;

  public static getExistingInstance(): WebSocketConnection | null { return WebSocketConnection.instance; }
  public static getExistingConnectedInstance(): WebSocketConnection | null { return WebSocketConnection.instance?.isConnected() ? WebSocketConnection.instance : null; }

  // Get the singleton instance
  public static getInstance(
    serverUrl?: string,
    joinToken?: string
  ): WebSocketConnection {
    if (!WebSocketConnection.instance && serverUrl) {
      console.log("[WebSocketConnection] creating connection to ", serverUrl, " token:", joinToken?.substring(0, 10));
      WebSocketConnection.instance = new WebSocketConnection(
        serverUrl,
        joinToken || null
      );
      //startAutoReconnectNow();
    } else if (
      WebSocketConnection.instance &&
      serverUrl &&
      WebSocketConnection.instance.serverUrl !== serverUrl
    ) {
      // If the URL has changed, disconnect the old connection and create a new one
      WebSocketConnection.instance.disconnect();
      WebSocketConnection.instance.serverUrl = serverUrl;
      WebSocketConnection.instance.joinToken = joinToken || null;
    } else if (
      WebSocketConnection.instance &&
      serverUrl &&
      WebSocketConnection.instance.joinToken !== joinToken
    ) {
      // If the URL has changed, disconnect the old connection and create a new one
      WebSocketConnection.instance.disconnect();
      WebSocketConnection.instance.serverUrl = serverUrl;
      WebSocketConnection.instance.joinToken = joinToken || null;
    }

    return WebSocketConnection.instance!;
  }

  private constructor(serverUrl: string, joinToken: string | null) {
    this.serverUrl = serverUrl;
    this.joinToken = joinToken;
    console.log(
      "[WebSocketConnection] constructor: ", this.serverUrl,
      " ", this.joinToken);
  }

  public addStatusListener(key: string, callback: ConnectionStatusListener): void {
    this.statusListeners.add(key, callback);
  }

  public removeStatusListener(key: string): void {
    this.statusListeners.remove(key);
  }

  private notifyStatusListeners(ofStatus: ConnectionStatus): void {
    this.statusListeners.notify(ofStatus);
  }

  public addReconnectCountdownListener(key: string, callback: ReconnectCountdownListener): void {
    this.reconnectCountdownListeners.add(key, callback);
  }

  public removeReconnectCountdownListener(key: string): void {
    this.reconnectCountdownListeners.remove(key);
  }

  private notifyReconnectCountdownListeners(ofCount: number): void {
    this.reconnectCountdownListeners.notify(ofCount);
  }

  public addSpeakersListener(key: string, callback: SpeakersListener): void {
    this.speakersListeners.add(key, callback);
  }

  public removeSpeakersListener(key: string): void {
    this.speakersListeners.remove(key);
  }

  private notifySpeakersListeners(ofSpeakers: AudioMessage_SpeakerInfo[]): void {
    this.speakersListeners.notify(ofSpeakers);
  }

  public addFocussedSpeakerListener(key: string, callback: FocussedSpeakerListener): void {
    this.focussedSpeakerListeners.add(key, callback);
  }

  public removeFocussedSpeakerListener(key: string): void {
    this.focussedSpeakerListeners.remove(key);
  }

  private notifyFocussedSpeakerListeners(ofSpeaker: AudioMessage_SpeakerInfo | undefined): void {
    this.focussedSpeakerListeners.notify(ofSpeaker);
  }

  public addListeningStatusListener(key: string, callback: ListeningStatusListener): void {
    this.listeningStatusListeners.add(key, callback);
  }

  public removeListeningStatusListener(key: string): void {
    this.listeningStatusListeners.remove(key);
  }

  private notifyListeningStatusListeners(ofIsListening: boolean): void {
    this.listeningStatusListeners.notify(ofIsListening);
  }

  public addHealthStatusListener(key: string, callback: HealthStatusListener): void {
    this.healthStatusListeners.add(key, callback);
  }

  public removeHealthStatusListener(key: string): void {
    this.healthStatusListeners.remove(key);
  }

  private notifyHealthStatusListeners(ofHealthStatus: AudioMessage_ServerInfo_HealthStatus): void {
    this.healthStatusListeners.notify(ofHealthStatus);
  }


  public addCommandListener(key: string, type: AudioMessage_Command_Type, callback: CommandListener): void {
    let reg = this.commandListeners.get(type);
    if (!reg) {
      reg = new ListenerRegistry<CommandListener>();
      this.commandListeners.set(type, reg);
    }
    reg.add(key, callback);
  }

  public removeCommandListener(key: string, type: AudioMessage_Command_Type): void {
    const reg = this.commandListeners.get(type);
    if (reg) reg.remove(key);
  }

  private notifyCommandListeners(cmd: AudioMessage_Command): void {
    if (cmd.type !== undefined) {
      const reg = this.commandListeners.get(cmd.type);
      if (reg) reg.notify(cmd);
    }
  }

  public getReconnectCountdown(): number {
    return this.reconnectCountdown;
  }

  public isAutoReconnectEnabled(): boolean {
    return this.autoReconnectEnabled;
  }

  public setAutoReconnectEnabled(enabled: boolean): void {
    console.log("[WebSocketConnection] setAutoReconnectEnabled", enabled);
    this.currentReconnectDelay = 1000;
    this.autoReconnectEnabled = enabled;

    // If disabled while in auto-reconnecting state, cancel the reconnection
    if (!enabled && this.status === "auto-reconnecting") {
      this.cancelReconnect();
      this.setStatus("disconnected");
    }
  }

  public startAutoReconnectNow(): void {
    // Only start auto-reconnect if it's enabled and we're not already connected or reconnecting
    if (
      this.autoReconnectEnabled &&
      this.status !== "connected" &&
      this.status !== "connecting" &&
      this.status !== "handshaking" &&
      this.status !== "auto-reconnecting"
    ) {
      // Reset the reconnection delay to start from the beginning
      this.currentReconnectDelay = 1000;

      // Start the auto-reconnect process
      this.startAutoReconnect();
    }
  }

  public setMaxReconnectDelay(maxDelayMs: number): void {
    this.maxReconnectDelay = maxDelayMs;
  }

  public getIsServerListening(): boolean {
    return this.isServerListening;
  }

  public getStatus(): ConnectionStatus {
    return this.status;
  }

  public isConnected(): boolean {
    return this.status === "connected";
  }

  public getErrorMessage(): string | undefined {
    return this.errorMessage;
  }

  private setStatus(newStatus: ConnectionStatus): void {
    console.log("[WebSocketConnection] setStatus", newStatus);
    this.status = newStatus;
    this.notifyStatusListeners(newStatus);
  }

  private updateReconnectCountdown(countdown: number): void {
    this.reconnectCountdown = countdown;
    this.notifyReconnectCountdownListeners(countdown);
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.updateReconnectCountdown(0);
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
  }

  private handleConnectionTimeout(): void {
    console.warn("[WebSocketConnection] connection attempt timed out after",
      this.CONNECTION_TIMEOUT_MS, "ms");

    // Only handle timeout if we're still in connecting state
    if (this.status === "connecting") {
      // Force close the socket if it exists
      if (this.socket) {
        // Remove event handlers to prevent them from firing during forced close
        this.socket.onopen = null;
        this.socket.onclose = null;
        this.socket.onmessage = null;
        this.socket.onerror = null;
        this.socket.close();
        this.socket = null;
      }

      this.errorMessage = "Connection attempt timed out";
      this.setStatus("error");

      // Start auto-reconnect process if enabled
      if (this.autoReconnectEnabled) {
        this.startAutoReconnect();
      }
    }
  }

  private startAutoReconnect(): void {
    console.log(
      "[WebSocketConnection] startAutoReconnect autoReconnectEnabled: ",
      this.autoReconnectEnabled, " status: ", this.status,
      " currentReconnectDelay: ", this.currentReconnectDelay
    );

    // Don't auto-reconnect if it's disabled
    if (!this.autoReconnectEnabled) {
      this.setStatus("disconnected");
      return;
    }

    this.setStatus("auto-reconnecting");

    // Start the reconnection timer
    const startCountdown = Math.floor(this.currentReconnectDelay / 1000);
    this.updateReconnectCountdown(startCountdown);

    // Set up a countdown timer that updates every second
    const countdownInterval = setInterval(() => {
      const newCountdown = this.reconnectCountdown - 1;
      this.updateReconnectCountdown(newCountdown);

      if (newCountdown <= 0) {
        clearInterval(countdownInterval);
      }
    }, 1000);

    this.reconnectTimer = setTimeout(() => {
      if (this.reconnectTimer) {
        clearInterval(countdownInterval);
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;

        // Attempt to reconnect
        console.log("[WebSocketConnection] timer triggered, attempting to reconnect");
        this.connect();
      }

      // If this attempt fails, the onclose or onerror handlers will trigger
      // another reconnection attempt with increased delay
    }, this.currentReconnectDelay);

    // Increase the delay for the next attempt (exponential backoff)
    this.currentReconnectDelay = Math.min(this.currentReconnectDelay * 2, this.maxReconnectDelay);
  }

  public connect(): void {
    console.log("[WebSocketConnection] starting connect");

    // Cancel any pending reconnection
    this.cancelReconnect();

    // Clear any existing connection timeout
    this.clearConnectionTimeout();

    this.errorMessage = undefined;
    const [jwt, tokenType] = getDecryptedJwtAndType();
    if (!jwt) {
      console.warn("[WebSocketConnection] No JWT available for WebSocket connection");
    }
    let token_param = "";
    switch (tokenType) {
      case TokenType.JOIN:
        token_param = "join_token";
        break;
      case TokenType.STYTCH:
        token_param = "token";
        break;
      default:
        console.warn("[WebSocketConnection] No token type detected");
        token_param = "token";
    }

    // Create WebSocket connection with JWT in query parameter
    let wsUrlWithAuth = this.serverUrl;

    if (jwt != null) { // covers undefined and null
      wsUrlWithAuth += (this.serverUrl.includes("?") ? "&" : "?") + `${token_param}=${jwt}`;
    }

    console.log("[WebSocketConnection] connecting to", this.serverUrl);

    this.setStatus("connecting");

    // Set connection timeout
    this.connectionTimeout = setTimeout(() => {
      this.handleConnectionTimeout();
    }, this.CONNECTION_TIMEOUT_MS);

    const ws = new WebSocket(wsUrlWithAuth);

    ws.onopen = () => {
      console.log("[WebSocketConnection] WebSocket Connected, starting handshake");
      this.clearConnectionTimeout();
      this.setStatus("handshaking");

      // Start handshake by sending connect message
      const connectMessage = createConnectMessage(this.serverUrl);

      // Send the initial connect message
      console.log("[WebSocketConnection] Sending initial connect message: ", connectMessage);
      const binary = AudioMessage.toBinary(connectMessage);
      ws.send(binary);
    };

    ws.onmessage = async (event) => {
      try {
        // Handle binary messages as protobuf
        if (event.data instanceof Blob) {
          const arrayBuffer = await event.data.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          const message = AudioMessage.fromBinary(uint8Array);
          //console.log('--- Received AudioMessage:', message);

          if (message.type === AudioMessage_Type.SERVER_TO_CLIENT &&
            message.infoBlock?.oneofKind === "serverInfo" &&
            message.infoBlock.serverInfo.clientDisplayInfo !== undefined) {
            console.log("[WebSocketConnection] Received client display info message:", message);
          }

          if (this.status === "handshaking") {
            // Handle handshake response
            const result = isServerConnected(message);
            if (result.connected) {
              this.setStatus("connected");
              console.log("[WebSocketConnection] Handshake complete - connection established");

              // This means if/when we DO disconnect, auto-reconnect will start at the beginning
              this.currentReconnectDelay = 1000;

              // Immediately send the clientInfoState, whatever it is.
              const clientInfoPulse = createOutgoingAudioMessage();
              console.log("[WebSocketConnection] Sending client info pulse:", clientInfoPulse);
              const binary = AudioMessage.toBinary(clientInfoPulse);
              ws.send(binary);

              // Ensure a talkable session in single-user mode.
              // if (this.singleUserMode) {
              //   const ensureMsg = createEnsureTalkableSessionMessage();
              //   console.log("Single user mode! Sending ensure talkable connection message.");
              //   this.sendMessage(ensureMsg);
              // }
            } else {
              this.errorMessage = result.error;
              this.disconnect("error");
              console.log("[WebSocketConnection] Handshake ERROR:", result.error);
            }
          } else {
            // Handle server info messages
            if (
              message.type === AudioMessage_Type.SERVER_TO_CLIENT &&
              message.infoBlock?.oneofKind === "serverInfo"
            ) {
              const isListening = !!message.infoBlock.serverInfo.listening;
              if (message.infoBlock.serverInfo.listening !== undefined && this.isServerListening !== isListening) {
                // console.log("[WebSocketConnection] Got Message with server listening change: ", message)
                this.isServerListening = isListening;
                this.notifyListeningStatusListeners(isListening);
              }

              if (message.infoBlock.serverInfo.speakers) {
                this.speakers = message.infoBlock.serverInfo.speakers;
                // console.log("[WebSocketConnection] Received speakers:", this.speakers);
                this.notifySpeakersListeners(this.speakers);
                const newFocussedSpeaker = this.speakers.find(
                  (s) => s.focused === true
                );
                if (
                  this.focussedSpeaker === undefined ||
                  newFocussedSpeaker === undefined ||
                  newFocussedSpeaker.uid !== this.focussedSpeaker.uid
                ) {
                  this.focussedSpeaker = newFocussedSpeaker;
                  this.notifyFocussedSpeakerListeners(this.focussedSpeaker);
                }
              }

              if (message.infoBlock.serverInfo.healthStatus !== undefined) {
                console.log("[WebSocketConnection] Received health status:", message.infoBlock.serverInfo.healthStatus);
                this.notifyHealthStatusListeners(message.infoBlock.serverInfo.healthStatus);
              }
            }
          }

          // dispatch commands
          if (message.commands.length > 0) {
            console.debug("[WebSocketConnection] Received commands:", message.commands);
            for (const cmd of message.commands) {
              this.notifyCommandListeners(cmd);
            }
          }
        }
      } catch (error) {
        console.error("[WebSocketConnection] Error handling WebSocket message:", error);
      }
    };

    ws.onclose = (event: CloseEvent) => {
      console.log(`[WebSocketConnection] Disconnected: code=${event.code}, reason='${event.reason}', wasClean=${event.wasClean}`);
      this.setStatus("disconnected");
      this.startAutoReconnect();
    };

    ws.onerror = (error) => {
      console.error("[WebSocketConnection] Error:", error);
      this.clearConnectionTimeout();
      this.setStatus("error");
      ws.close();
    };

    this.socket = ws;
  }

  public disconnect(newStatus: ConnectionStatus = "disconnected"): void {
    console.log("[WebSocketConnection] disconnect");
    // Cancel any pending reconnection
    this.cancelReconnect();

    // Clear any connection timeout
    this.clearConnectionTimeout();

    // When manually disconnecting, disable auto-reconnect
    //this.autoReconnectEnabled = false;

    if (this.socket) {
      this.socket.close();
    }
    this.setStatus(newStatus);
  }

  public sendMessage(message: AudioMessage): void {
    //console.log("[WebSocketConnection] sendMessage called with message:", message);
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const binary = AudioMessage.toBinary(message);
      this.socket.send(binary);
    } else {
      console.error("[WebSocketConnection] not ready");
    }
  }

  public disconnectAndStop(): void {
    console.log("[WebSocketConnection] disconnectAndStop");
    this.setAutoReconnectEnabled(false);
    this.disconnect();
  }
}
